import React, { useState, useRef, useEffect } from "react";
import { Button, Tooltip } from "antd";
import { SoundOutlined, LoadingOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import theme from "../themes";

interface VoicePreviewButtonProps {
  voiceType: string;
}

const VoicePreviewButton: React.FC<VoicePreviewButtonProps> = ({
  voiceType,
}) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlay = async () => {
    if (isPlaying || isLoading) return;

    try {
      setIsLoading(true);

      // Create audio element if it doesn't exist
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.addEventListener("ended", () => setIsPlaying(false));
        audioRef.current.addEventListener("error", () => {
          setIsPlaying(false);
          setIsLoading(false);
        });
      }

      // Set the audio source based on voice type
      // Use public directory so files are served at /audios/*.mp3
      const audioPath = `/audios/${voiceType}.mp3`;
      audioRef.current.src = audioPath;

      // Play the audio
      await audioRef.current.play();
      setIsPlaying(true);
      setIsLoading(false);
    } catch (error) {
      console.error("Error playing voice preview:", error);
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    handleStop();
  }, [voiceType]);

  return (
    <Tooltip
      title={
        isPlaying
          ? t("agents.agentForm.stopPreview")
          : t("agents.agentForm.voicePreview")
      }
    >
      <StyledButton
        type="text"
        size="small"
        onClick={isPlaying ? handleStop : handlePlay}
        $isPlaying={isPlaying}
      >
        {isLoading ? (
          <LoadingOutlined />
        ) : isPlaying ? (
          <StopIcon />
        ) : (
          <SoundOutlined />
        )}
      </StyledButton>
    </Tooltip>
  );
};

const StyledButton = styled(Button)<{ $isPlaying: boolean }>`
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${({ $isPlaying }) =>
    $isPlaying ? theme.colors.green1 : theme.colors.grey1};
`;

const StopIcon = styled.div`
  width: 8px;
  height: 8px;
  background: currentColor;
  border-radius: 1px;
`;

export default VoicePreviewButton;
